import { lazy, Suspense } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';

// project import
import MainLayout from 'layout/MainLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import TourProvider from 'contexts/TourContext';
import PaidUserGuard from '../utils/route-guard/PaidUserGuard';
import AnalyzerGuard from '../utils/route-guard/AnalyzerGuard';
import EaConnectGuard from '../utils/route-guard/EaConnectGuard';
import ErrorBoundary from 'components/ErrorBoundary';
import DashboardPageSkeleton from 'sections/dashboard/components/loading-skeleton/DashboardPageSkeleton';
import AccountsDashboardPageSkeleton from 'sections/dashboard/components/loading-skeleton/AccountsDashboardPageSkeleton';
import AllTradesPageSkeleton from 'sections/dashboard/components/loading-skeleton/AllTradesPageSkeleton';
import CopyTradingDashboardPageSkeleton from 'sections/dashboard/components/loading-skeleton/CopyTradingDashboardPageSkeleton';
import CopierTemplatesPageSkeleton from 'sections/dashboard/components/loading-skeleton/CopierTemplatesPageSkeleton';
import CopierLogsPageSkeleton from 'sections/dashboard/components/loading-skeleton/CopierLogsPageSkeleton';
import SentimentsPageSkeleton from 'sections/dashboard/components/loading-skeleton/SentimentsPageSkeleton';
import IntegrationPageSkeleton from 'sections/dashboard/components/loading-skeleton/IntegrationsSkeleton';
import EconomicCalendarPageSkeleton from 'sections/dashboard/components/loading-skeleton/EconomicCalendarPageSkeleton';
import SubscriptionsPageSkeleton from 'sections/dashboard/components/loading-skeleton/SubscriptionsPageSkeleton';
import SettingsPageSkeleton from 'sections/dashboard/components/loading-skeleton/SettingsPageSkeleton';
import SubscriptionFreezeRouteGuard from 'features/subscription-freeze/SubscriptionFreezeRouteGuard';
import { FREEZE_RESUME_PATH, FREEZE_STATUS_PATH } from 'features/subscription-freeze/paths';
import { featureFlags } from 'env';

// const ProfilePage = Loadable(lazy(() => import('pages/profile/ProfilePage')));
// const ProfileViewSection = Loadable(lazy(() => import('sections/profile/ProfileViewSection')));
// const ProfileSettingsSection = Loadable(lazy(() => import('sections/profile/ProfileSettingsSection')));

const UserAccountSettingsPage = lazy(() => import('pages/account-settings/AccountSettingsPage'));
const MarketNewsPage = Loadable(lazy(() => import('pages/trading-tools/MarketNewsPage')));

const DashboardPage = Loadable(lazy(() => import('pages/dashboard/DashboardPage')));
const WelcomeTourPage = Loadable(lazy(() => import('pages/welcome-tour/WelcomeTourPage')));
const AccountsDashboardPage = Loadable(lazy(() => import('pages/accounts/AccountsDashboardPage')));
const TradovateReconnectPage = Loadable(lazy(() => import('pages/accounts/TradovateReconnectPage')));
const AccountAnalyticsPage = Loadable(lazy(() => import('pages/accounts/AccountAnalyticsPage')));
const TradeListingPage = lazy(() => import('pages/trade/TradeListingPage'));
const EconomicCalendarPage = lazy(() => import('pages/economic-calendar/EconomicCalendarPage'));

const AccountSettingsPage = Loadable(lazy(() => import('pages/accounts/AccountSettingsPage')));
const ProfileOverviewSection = Loadable(lazy(() => import('sections/accounts/settings/ProfileOverviewSection')));
const AccountManagementSection = Loadable(lazy(() => import('sections/accounts/settings/AccountManagementSection')));
const EquityProtectorSection = Loadable(lazy(() => import('sections/accounts/settings/EquityProtectorSection')));
const TradingSymbolSection = Loadable(lazy(() => import('sections/accounts/settings/TradingSymbolsSection')));
const ImportSymbolsSection = Loadable(lazy(() => import('sections/accounts/settings/ImportSymbolsSection')));
const McpSection = Loadable(lazy(() => import('sections/accounts/settings/McpSection')));
const McpHubPage = Loadable(lazy(() => import('pages/mcp/McpHubPage')));
const McpDocsPage = Loadable(lazy(() => import('pages/mcp/McpDocsPage')));

const MapSymbolsPage = Loadable(lazy(() => import('pages/trade-copier/MapSymbolsPage')));
const CopyTradingDashboardPage = lazy(() => import('pages/trade-copier/CopyTradingDashboardPage'));
const CopierLogsPage = lazy(() => import('pages/trade-copier/CopierLogsPage'));
const CopierTemplatesPage = lazy(() => import('pages/trade-copier/CopierTemplatesPage'));
const RoomsPage = lazy(() => import('pages/trade-copier/RoomsPage'));

const IntegrationPage = lazy(() => import('pages/integration/IntegrationPage'));
const EmailIntegrationPage = Loadable(lazy(() => import('pages/integration/EmailIntegrationPage')));
const EmailConfigurationSection = Loadable(lazy(() => import('sections/integration/email/EmailConfigurationSection')));
const NotificationSection = Loadable(lazy(() => import('sections/integration/email/NotificationSection')));

const TelegramIntegrationPage = Loadable(lazy(() => import('pages/integration/TelegramIntegrationPage')));
const TelegramConfigurationSection = Loadable(lazy(() => import('sections/integration/telegram/TelegramConfigurationSection')));
const TelegramNotificationSection = Loadable(lazy(() => import('sections/integration/telegram/TelegramNotificationSection')));

const DedicatedEnvironmentPage = Loadable(lazy(() => import('pages/integration/DedicatedEnvironmentPage')));
const DedicatedEnvConfigurationSection = Loadable(
  lazy(() => import('sections/integration/dedicated-env/DedicatedEnvConfigurationSection'))
);
const DedicatedEnvAccountSection = Loadable(lazy(() => import('sections/integration/dedicated-env/DedicatedEnvAccountSection')));

const SubscriptionsPage = lazy(() => import('pages/account-settings/SubscriptionsPage'));
const PaymentMethodTabSection = Loadable(lazy(() => import('sections/account-settings/subscriptions/PaymentMethodTabSection')));
const InvoiceTabSection = Loadable(lazy(() => import('sections/account-settings/subscriptions/InvoiceTabSection')));
const CryptoPaymentPending = Loadable(lazy(() => import('sections/account-settings/subscriptions/CryptoPaymentPending')));
const SubscriptionFreezeStatusPage = Loadable(lazy(() => import('features/subscription-freeze/pages/SubscriptionFreezeStatusPage')));
const SubscriptionFreezeResumePage = Loadable(lazy(() => import('features/subscription-freeze/pages/SubscriptionFreezeResumePage')));

const UnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction')));

const AnalyzerPerformancePage = Loadable(lazy(() => import('pages/analyzer/AnalyzerPerformancePage')));
const AnalyzerPortfolioPage = Loadable(lazy(() => import('pages/analyzer/AnalyzerPortfolioPage')));
const AnalyzerAnalysisHubPage = Loadable(lazy(() => import('pages/analyzer/AnalyzerAnalysisHubPage')));
const SentimentsPage = lazy(() => import('pages/sentiments/SentimentsPage'));
const PalettePreview = Loadable(lazy(() => import('pages/PalettePreview')));
const FeatureRequestsPage = Loadable(lazy(() => import('pages/feature-requests/FeatureRequestsPage')));
const DeployBotPage = Loadable(lazy(() => import('pages/ea-connect/DeployBotPage')));
const MyBotsPage = Loadable(lazy(() => import('pages/ea-connect/MyBotsPage')));
const BotDetailPage = Loadable(lazy(() => import('pages/ea-connect/BotDetailPage')));

// Shared billing-route block used by Main, Beta, and Admin route trees.
// Single source of truth for the /account-settings/billing subtree so adding
// or removing a child route (e.g. crypto-pending) is a one-line change
// instead of a three-place edit that drifts.
const accountBillingRoute = {
  path: 'account-settings/billing',
  element: (
    <Suspense fallback={<SubscriptionsPageSkeleton />}>
      <SubscriptionsPage />
    </Suspense>
  ),
  children: [
    {
      path: 'payment-methods',
      element: <PaymentMethodTabSection />
    },
    {
      path: 'invoices',
      element: <InvoiceTabSection />
    },
    {
      // Landing page after Coingate hosted checkout redirect. Polls
      // hasPendingCryptoPayment until the backend cron finalises the
      // subscription, then routes back to the subscriptions list.
      path: 'crypto-pending',
      element: <CryptoPaymentPending />
    }
  ]
};

// Not registered at all while the flag is off.
const subscriptionFreezeRoutes = featureFlags.subscriptionFreeze
  ? [
      { path: FREEZE_STATUS_PATH, element: <SubscriptionFreezeStatusPage /> },
      { path: FREEZE_RESUME_PATH, element: <SubscriptionFreezeResumePage /> }
    ]
  : [];

// ==============================|| MAIN ROUTING ||============================== //

// Preserves the :botId when bouncing old my-bots deep links to /fleet.
const LegacyBotRedirect = () => {
  const { botId } = useParams();
  return <Navigate to={`/ea-connect/fleet/${botId}`} replace />;
};

// Shared EA Connect route block, used by every route tree (Main, Beta,
// Admin). Each real page is wrapped in EaConnectGuard: the sidebar group is
// hidden by the same canSeeEaConnect predicate, but hiding a nav item is not
// a gate — without the guard these pages stayed reachable by URL while the
// rollout is concealed. The redirects need no guard of their own; they all
// funnel into a guarded page.
const eaConnectRoutes = [
  {
    path: 'ea-connect/deploy',
    element: (
      <EaConnectGuard>
        <DeployBotPage />
      </EaConnectGuard>
    )
  },
  {
    path: 'ea-connect/fleet',
    element: (
      <EaConnectGuard>
        <MyBotsPage />
      </EaConnectGuard>
    )
  },
  {
    path: 'ea-connect/fleet/:botId',
    element: (
      <EaConnectGuard>
        <BotDetailPage />
      </EaConnectGuard>
    )
  },
  // Legacy paths — redirect so old links and history keep working.
  {
    path: 'ea-connect/my-bots',
    element: <Navigate to="/ea-connect/fleet" replace />
  },
  {
    path: 'ea-connect/my-bots/:botId',
    element: <LegacyBotRedirect />
  },
  {
    path: 'ea-connect',
    element: <Navigate to="/ea-connect/fleet" replace />
  }
];

const MainRoutes = {
  path: '/',
  errorElement: <ErrorBoundary />,
  // Layout route: the tour overlays real pages, so its state must sit above
  // every route it drives.
  element: (
    <TourProvider>
      <Outlet />
    </TourProvider>
  ),
  children: [
    {
      // Full-screen. The tour draws its own shell; the REAL app menu slides in
      // over its rail when a step hands off to a product page.
      path: 'welcome-tour',
      element: (
        <AuthGuard>
          <Outlet />
        </AuthGuard>
      ),
      errorElement: <ErrorBoundary />,
      children: [{ path: '', element: <WelcomeTourPage /> }]
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <SubscriptionFreezeRouteGuard>
            <MainLayout />
          </SubscriptionFreezeRouteGuard>
        </AuthGuard>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        {
          path: 'dashboard',
          element: (
            <Suspense fallback={<DashboardPageSkeleton />}>
              <DashboardPage />
            </Suspense>
          )
        },
        {
          path: 'economic-calendar',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<EconomicCalendarPageSkeleton />}>
                <EconomicCalendarPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts',
          element: (
            <Suspense fallback={<AccountsDashboardPageSkeleton />}>
              <AccountsDashboardPage />
            </Suspense>
          )
        },
        {
          path: 'ai-connect',
          element: <McpHubPage />
        },
        {
          path: 'ai-connect/docs',
          element: <McpDocsPage />
        },
        {
          // Legacy URL — MCP shipped as /mcp before the AI Connect rebrand.
          path: 'mcp',
          element: <Navigate to="/ai-connect" replace />
        },
        {
          path: 'account-trades',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<AllTradesPageSkeleton />}>
                <TradeListingPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts/reconnect',
          element: <TradovateReconnectPage />
        },
        {
          path: 'accounts/:reAuth',
          element: <AccountsDashboardPage />
        },
        {
          path: 'accounts/analytics/:id',
          element: (
            <PaidUserGuard>
              <AccountAnalyticsPage />
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts/settings/:id',
          element: (
            <PaidUserGuard>
              <AccountSettingsPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <ProfileOverviewSection />
            },
            {
              path: 'overview',
              element: <ProfileOverviewSection />
            },
            {
              path: 'account-management',
              element: <AccountManagementSection />
            },
            {
              path: 'equity-protector',
              element: <EquityProtectorSection />
            },
            {
              path: 'trading-symbols',
              element: <TradingSymbolSection />
            },
            {
              path: 'import-symbols',
              element: <ImportSymbolsSection />
            },
            {
              path: 'ai-connect',
              element: <McpSection />
            }
          ]
        },
        {
          path: 'trade-copier/copier-settings',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopyTradingDashboardPageSkeleton />}>
                <CopyTradingDashboardPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/copier-logs',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopierLogsPageSkeleton />}>
                <CopierLogsPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/rooms',
          element: (
            <Suspense fallback={null}>
              <RoomsPage />
            </Suspense>
          )
        },
        {
          path: 'trade-copier/templates',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopierTemplatesPageSkeleton />}>
                <CopierTemplatesPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/copier-settings/map-symbols',
          element: (
            <PaidUserGuard>
              <MapSymbolsPage />
            </PaidUserGuard>
          )
        },
        {
          path: 'integration',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<IntegrationPageSkeleton />}>
                <IntegrationPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'integration/email',
          element: (
            <PaidUserGuard>
              <EmailIntegrationPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <EmailConfigurationSection />
            },
            {
              path: 'configuration',
              element: <EmailConfigurationSection />
            },
            {
              path: 'settings',
              element: <NotificationSection />
            }
          ]
        },
        {
          path: 'integration/telegram',
          element: (
            <PaidUserGuard>
              <TelegramIntegrationPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <TelegramConfigurationSection />
            },
            {
              path: 'configuration',
              element: <TelegramConfigurationSection />
            },
            {
              path: 'settings',
              element: <TelegramNotificationSection />
            }
          ]
        },
        {
          path: 'integration/dedicated-environment',
          element: (
            <PaidUserGuard>
              <DedicatedEnvironmentPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'env-configuration',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'account-configuration',
              element: <DedicatedEnvAccountSection />
            }
          ]
        },
        {
          path: 'analyzer/performance',
          element: (
            <AnalyzerGuard>
              <AnalyzerPerformancePage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'analyzer/portfolio',
          element: (
            <AnalyzerGuard>
              <AnalyzerPortfolioPage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'analyzer/analysis-hub',
          element: (
            <AnalyzerGuard>
              <AnalyzerAnalysisHubPage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'sentiments',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<SentimentsPageSkeleton />}>
                <SentimentsPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        { path: 'color-palette', element: <PalettePreview /> },
        ...eaConnectRoutes,
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <SubscriptionFreezeRouteGuard>
            <MainLayout />
          </SubscriptionFreezeRouteGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        accountBillingRoute,
        ...subscriptionFreezeRoutes,
        {
          path: 'account-settings',
          element: (
            <Suspense fallback={<SettingsPageSkeleton />}>
              <UserAccountSettingsPage />
            </Suspense>
          )
        },
        {
          path: 'analyzer/performance',
          element: (
            <AnalyzerGuard>
              <AnalyzerPerformancePage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'analyzer/portfolio',
          element: (
            <AnalyzerGuard>
              <AnalyzerPortfolioPage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'analyzer/analysis-hub',
          element: (
            <AnalyzerGuard>
              <AnalyzerAnalysisHubPage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'tools/market-news',
          element: <MarketNewsPage />,
          children: []
        },
        {
          path: 'feature-requests',
          element: <FeatureRequestsPage />
        },
        ...eaConnectRoutes,
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    }
  ]
};

export const MainRoutesBeta = {
  path: '/',
  errorElement: <ErrorBoundary />,
  element: (
    <TourProvider>
      <Outlet />
    </TourProvider>
  ),
  children: [
    {
      // Full-screen. The tour draws its own shell; the REAL app menu slides in
      // over its rail when a step hands off to a product page.
      path: 'welcome-tour',
      element: (
        <AuthGuard>
          <Outlet />
        </AuthGuard>
      ),
      errorElement: <ErrorBoundary />,
      children: [{ path: '', element: <WelcomeTourPage /> }]
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <SubscriptionFreezeRouteGuard>
            <MainLayout />
          </SubscriptionFreezeRouteGuard>
        </AuthGuard>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        {
          path: 'dashboard',
          element: <DashboardPage />
        },
        {
          path: 'economic-calendar',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<EconomicCalendarPageSkeleton />}>
                <EconomicCalendarPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts',
          element: <AccountsDashboardPage />
        },
        {
          path: 'ai-connect',
          element: <McpHubPage />
        },
        {
          path: 'ai-connect/docs',
          element: <McpDocsPage />
        },
        {
          // Legacy URL — MCP shipped as /mcp before the AI Connect rebrand.
          path: 'mcp',
          element: <Navigate to="/ai-connect" replace />
        },
        {
          path: 'account-trades',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<AllTradesPageSkeleton />}>
                <TradeListingPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts/reconnect',
          element: <TradovateReconnectPage />
        },
        {
          path: 'accounts/:reAuth',
          element: <AccountsDashboardPage />
        },
        {
          path: 'accounts/analytics/:id',
          element: (
            <PaidUserGuard>
              <AccountAnalyticsPage />
            </PaidUserGuard>
          )
        },
        {
          path: 'accounts/settings/:id',
          element: (
            <PaidUserGuard>
              <AccountSettingsPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <ProfileOverviewSection />
            },
            {
              path: 'overview',
              element: <ProfileOverviewSection />
            },
            {
              path: 'account-management',
              element: <AccountManagementSection />
            },
            {
              path: 'equity-protector',
              element: <EquityProtectorSection />
            },
            {
              path: 'trading-symbols',
              element: <TradingSymbolSection />
            },
            {
              path: 'import-symbols',
              element: <ImportSymbolsSection />
            },
            {
              path: 'ai-connect',
              element: <McpSection />
            }
          ]
        },
        {
          path: 'trade-copier/copier-settings',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopyTradingDashboardPageSkeleton />}>
                <CopyTradingDashboardPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/copier-logs',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopierLogsPageSkeleton />}>
                <CopierLogsPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/rooms',
          element: (
            <Suspense fallback={null}>
              <RoomsPage />
            </Suspense>
          )
        },
        {
          path: 'trade-copier/templates',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<CopierTemplatesPageSkeleton />}>
                <CopierTemplatesPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'trade-copier/copier-settings/map-symbols',
          element: (
            <PaidUserGuard>
              <MapSymbolsPage />
            </PaidUserGuard>
          )
        },
        {
          path: 'integration',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<IntegrationPageSkeleton />}>
                <IntegrationPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        {
          path: 'integration/email',
          element: (
            <PaidUserGuard>
              <EmailIntegrationPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <EmailConfigurationSection />
            },
            {
              path: 'configuration',
              element: <EmailConfigurationSection />
            },
            {
              path: 'settings',
              element: <NotificationSection />
            }
          ]
        },
        {
          path: 'integration/telegram',
          element: (
            <PaidUserGuard>
              <TelegramIntegrationPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <TelegramConfigurationSection />
            },
            {
              path: 'configuration',
              element: <TelegramConfigurationSection />
            },
            {
              path: 'settings',
              element: <TelegramNotificationSection />
            }
          ]
        },
        {
          path: 'integration/dedicated-environment',
          element: (
            <PaidUserGuard>
              <DedicatedEnvironmentPage />
            </PaidUserGuard>
          ),
          children: [
            {
              path: '',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'env-configuration',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'account-configuration',
              element: <DedicatedEnvAccountSection />
            }
          ]
        },
        {
          path: 'analyzer/performance',
          element: (
            <AnalyzerGuard>
              <AnalyzerPerformancePage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'analyzer/portfolio',
          element: (
            <AnalyzerGuard>
              <AnalyzerPortfolioPage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'analyzer/analysis-hub',
          element: (
            <AnalyzerGuard>
              <AnalyzerAnalysisHubPage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'sentiments',
          element: (
            <PaidUserGuard>
              <Suspense fallback={<SentimentsPageSkeleton />}>
                <SentimentsPage />
              </Suspense>
            </PaidUserGuard>
          )
        },
        { path: 'color-palette', element: <PalettePreview /> },
        ...eaConnectRoutes,
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <SubscriptionFreezeRouteGuard>
            <MainLayout />
          </SubscriptionFreezeRouteGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        accountBillingRoute,
        ...subscriptionFreezeRoutes,
        {
          path: 'account-settings',
          element: (
            <Suspense fallback={<SettingsPageSkeleton />}>
              <UserAccountSettingsPage />
            </Suspense>
          )
        },
        {
          path: 'analyzer/performance',
          element: (
            <AnalyzerGuard>
              <AnalyzerPerformancePage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'analyzer/portfolio',
          element: (
            <AnalyzerGuard>
              <AnalyzerPortfolioPage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'analyzer/analysis-hub',
          element: (
            <AnalyzerGuard>
              <AnalyzerAnalysisHubPage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'tools/market-news',
          element: <MarketNewsPage />,
          children: []
        },
        {
          path: 'feature-requests',
          element: <FeatureRequestsPage />
        },
        ...eaConnectRoutes,
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    }
  ]
};

export const MainRoutesAdmin = {
  path: '/',
  errorElement: <ErrorBoundary />,
  element: (
    <TourProvider>
      <Outlet />
    </TourProvider>
  ),
  children: [
    {
      // Full-screen. The tour draws its own shell; the REAL app menu slides in
      // over its rail when a step hands off to a product page.
      path: 'welcome-tour',
      element: (
        <AuthGuard>
          <Outlet />
        </AuthGuard>
      ),
      errorElement: <ErrorBoundary />,
      children: [{ path: '', element: <WelcomeTourPage /> }]
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <SubscriptionFreezeRouteGuard>
            <MainLayout />
          </SubscriptionFreezeRouteGuard>
        </AuthGuard>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        {
          path: 'dashboard',
          element: <DashboardPage />
        },
        {
          path: 'economic-calendar',
          element: (
            <Suspense fallback={<EconomicCalendarPageSkeleton />}>
              <EconomicCalendarPage />
            </Suspense>
          )
        },
        {
          path: 'accounts',
          element: <AccountsDashboardPage />
        },
        {
          path: 'ai-connect',
          element: <McpHubPage />
        },
        {
          path: 'ai-connect/docs',
          element: <McpDocsPage />
        },
        {
          // Legacy URL — MCP shipped as /mcp before the AI Connect rebrand.
          path: 'mcp',
          element: <Navigate to="/ai-connect" replace />
        },
        {
          path: 'account-trades',
          element: (
            <Suspense fallback={<AllTradesPageSkeleton />}>
              <TradeListingPage />
            </Suspense>
          )
        },
        {
          path: 'accounts/reconnect',
          element: <TradovateReconnectPage />
        },
        {
          path: 'accounts/:reAuth',
          element: <AccountsDashboardPage />
        },
        {
          path: 'accounts/analytics/:id',
          element: <AccountAnalyticsPage />
        },
        {
          path: 'accounts/settings/:id',
          element: <AccountSettingsPage />,
          children: [
            {
              path: '',
              element: <ProfileOverviewSection />
            },
            {
              path: 'overview',
              element: <ProfileOverviewSection />
            },
            {
              path: 'account-management',
              element: <AccountManagementSection />
            },
            {
              path: 'equity-protector',
              element: <EquityProtectorSection />
            },
            {
              path: 'trading-symbols',
              element: <TradingSymbolSection />
            },
            {
              path: 'import-symbols',
              element: <ImportSymbolsSection />
            },
            {
              path: 'ai-connect',
              element: <McpSection />
            }
          ]
        },
        {
          path: 'trade-copier/copier-settings',
          element: (
            <Suspense fallback={<CopyTradingDashboardPageSkeleton />}>
              <CopyTradingDashboardPage />
            </Suspense>
          )
        },
        {
          path: 'trade-copier/copier-logs',
          element: (
            <Suspense fallback={<CopierLogsPageSkeleton />}>
              <CopierLogsPage />
            </Suspense>
          )
        },
        {
          path: 'trade-copier/rooms',
          element: (
            <Suspense fallback={null}>
              <RoomsPage />
            </Suspense>
          )
        },
        {
          path: 'trade-copier/templates',
          element: (
            <Suspense fallback={<CopierTemplatesPageSkeleton />}>
              <CopierTemplatesPage />
            </Suspense>
          )
        },
        {
          path: 'trade-copier/copier-settings/map-symbols',
          element: <MapSymbolsPage />
        },
        {
          path: 'integration',
          element: (
            <Suspense fallback={<IntegrationPageSkeleton />}>
              <IntegrationPage />
            </Suspense>
          )
        },
        {
          path: 'integration/email',
          element: <EmailIntegrationPage />,
          children: [
            {
              path: '',
              element: <EmailConfigurationSection />
            },
            {
              path: 'configuration',
              element: <EmailConfigurationSection />
            },
            {
              path: 'settings',
              element: <NotificationSection />
            }
          ]
        },
        {
          path: 'integration/telegram',
          element: <TelegramIntegrationPage />,
          children: [
            {
              path: '',
              element: <TelegramConfigurationSection />
            },
            {
              path: 'configuration',
              element: <TelegramConfigurationSection />
            },
            {
              path: 'settings',
              element: <TelegramNotificationSection />
            }
          ]
        },
        {
          path: 'integration/dedicated-environment',
          element: <DedicatedEnvironmentPage />,
          children: [
            {
              path: '',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'env-configuration',
              element: <DedicatedEnvConfigurationSection />
            },
            {
              path: 'account-configuration',
              element: <DedicatedEnvAccountSection />
            }
          ]
        },
        {
          path: 'analyzer/performance',
          element: (
            <AnalyzerGuard>
              <AnalyzerPerformancePage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'analyzer/portfolio',
          element: (
            <AnalyzerGuard>
              <AnalyzerPortfolioPage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'analyzer/analysis-hub',
          element: (
            <AnalyzerGuard>
              <AnalyzerAnalysisHubPage />
            </AnalyzerGuard>
          )
        },
        {
          path: 'sentiments',
          element: (
            <Suspense fallback={<SentimentsPageSkeleton />}>
              <SentimentsPage />
            </Suspense>
          )
        },
        { path: 'color-palette', element: <PalettePreview /> },
        ...eaConnectRoutes,
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <SubscriptionFreezeRouteGuard>
            <MainLayout />
          </SubscriptionFreezeRouteGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'maintenance',
          element: <UnderConstruction />
        },
        accountBillingRoute,
        ...subscriptionFreezeRoutes,
        {
          path: 'account-settings',
          element: (
            <Suspense fallback={<SettingsPageSkeleton />}>
              <UserAccountSettingsPage />
            </Suspense>
          )
        },
        {
          path: 'analyzer',
          element: (
            <AnalyzerGuard>
              <AnalyzerPerformancePage />
            </AnalyzerGuard>
          ),
          children: [
            {
              path: 'performance',
              element: (
                <AnalyzerGuard>
                  <AnalyzerPerformancePage />
                </AnalyzerGuard>
              )
            },
            {
              path: 'portfolio',
              element: (
                <AnalyzerGuard>
                  <AnalyzerPortfolioPage />
                </AnalyzerGuard>
              )
            }
          ]
        },
        {
          path: 'tools/market-news',
          element: <MarketNewsPage />,
          children: []
        },
        {
          path: 'feature-requests',
          element: <FeatureRequestsPage />
        },
        ...eaConnectRoutes,
        {
          path: '*',
          element: <Navigate to="/dashboard" replace />
        }
      ]
    }
  ]
};

export default MainRoutes;
